import { useEffect, useState } from 'react';
import { Link, useParams, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, Download, CornerUpLeft, AlignLeft, Users } from 'lucide-react';
import { api } from '../../lib/api';
import PaperBody from '../../components/paper/PaperBody';
import PlainBody from '../../components/paper/PlainBody';
import { Label } from '../../components/u99/Label';
import Colophon, { Byline, KindEyebrow } from '../../components/u99/Colophon';

const btn = 'inline-flex items-center justify-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.25em] px-5 py-3 rounded-full border transition-colors duration-300';

export default function DocumentPage() {
  const { slug } = useParams();
  const { scrollTo } = useOutletContext();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setDoc(null);
    setError(false);
    api.get(`/documents/${slug}`).then((r) => { setDoc(r.data); document.title = `${r.data.title} — Documents`; }).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <main className="pt-40 pb-40 text-center px-6" data-testid="document-not-found">
        <p className="font-mono-precision text-xs tracking-[0.3em] text-amber-300">NOT IN THE LIBRARY</p>
        <h1 className="font-celestial text-3xl text-slate-100 mt-4">This document is not published</h1>
        <Link to="/u99/documents" className="inline-block mt-8 font-mono-precision text-[11px] uppercase tracking-[0.25em] text-slate-400 hover:text-amber-300">← Return to Documents</Link>
      </main>
    );
  }

  const hasFile = Boolean(doc?.file_url);

  return (
    <main className="min-h-screen" data-testid="document-page">
      {doc && (
        <>
          <header className="relative pt-32 pb-12 lg:pt-44 lg:pb-20 nebula-mesh overflow-hidden">
            {doc.hero_image && (
              <div className="absolute inset-0 pointer-events-none" data-testid="document-hero-image">
                <img src={doc.hero_image} alt={doc.hero_caption || ''} className="w-full h-full object-cover opacity-40 scale-105" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#02040a]/70 via-[#02040a]/60 to-[#02040a]" />
              </div>
            )}
            <div className="relative max-w-4xl mx-auto px-5 lg:px-10">
              <Link to="/u99/documents" className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-amber-300 transition-colors" data-testid="document-back-link"><ArrowLeft size={12} /> Documents</Link>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="mt-10 flex flex-wrap items-center gap-4">
                <KindEyebrow kind={doc.category} />
                <Label id={doc.label} size="xs" />
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} className="mt-6 font-celestial text-4xl sm:text-5xl lg:text-6xl font-light text-slate-100 leading-[1.05]" data-testid="document-title">{doc.title}</motion.h1>
              <Byline author={doc.author} />
              {doc.summary && <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl" data-testid="document-summary">{doc.summary}</p>}

              <div className="mt-10 flex flex-wrap gap-3" data-testid="document-actions">
                <button onClick={() => scrollTo('document-body')} className={`${btn} border-amber-400/50 text-amber-300 hover:bg-amber-400/10`} data-testid="document-view-btn"><Eye size={13} /> View</button>
                {hasFile ? (
                  <a href={doc.file_url} download={doc.file_name || true} target="_blank" rel="noreferrer" className={`${btn} border-white/15 text-slate-300 hover:text-slate-100 hover:border-white/40`} data-testid="document-download-btn"><Download size={13} /> Download {doc.file_type && `· ${doc.file_type}`}</a>
                ) : (
                  <span className={`${btn} border-white/[0.08] text-slate-600 cursor-not-allowed`} title="Original file not yet attached" data-testid="document-download-pending"><Download size={13} /> Download · pending</span>
                )}
                {doc.world_url && <Link to={doc.world_url} className={`${btn} border-fuchsia-400/40 text-fuchsia-300 hover:bg-fuchsia-400/10`} data-testid="document-world-btn"><Users size={13} /> The Characters</Link>}
                <Link to="/u99/documents" className={`${btn} border-transparent text-slate-500 hover:text-slate-200`} data-testid="document-return-btn"><CornerUpLeft size={13} /> Return</Link>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="document-meta">
                <span className="flex items-center gap-2"><AlignLeft size={12} /> {doc.word_count.toLocaleString()} words</span>
                <span>Updated {new Date(doc.updated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                {doc.file_name && <span className="text-slate-600">{doc.file_name}</span>}
              </div>
            </div>
          </header>

          <article id="document-body" className="max-w-4xl mx-auto px-5 lg:px-10 pb-28 lg:pb-40 scroll-mt-24">
            {doc.hero_image && (
              <figure className="mb-14 rounded-3xl overflow-hidden border border-amber-400/20" data-testid="document-visual-anchor">
                <img src={doc.hero_image} alt={doc.hero_caption || `Visual anchor for ${doc.title}`} className="w-full h-auto block" loading="lazy" />
                {doc.hero_caption && <figcaption className="px-6 py-4 border-t border-amber-400/15 bg-[#04070f] font-mono-precision text-[10px] sm:text-[11px] uppercase tracking-[0.2em] leading-relaxed text-amber-200/80" data-testid="document-hero-caption">{doc.hero_caption}</figcaption>}
              </figure>
            )}
            <div className="border-t border-white/[0.07] pt-14">
              {doc.body_md && doc.body_format === 'text' && <PlainBody text={doc.body_md} />}
              {doc.body_md && doc.body_format !== 'text' && <PaperBody markdown={doc.body_md} lead />}
              {!doc.body_md && <p className="font-mono-precision text-xs tracking-[0.25em] text-slate-500" data-testid="document-empty">TEXT PENDING — THE AUTHOR'S FILE HAS NOT YET BEEN RECEIVED. NOTHING IS REPRODUCED HERE UNTIL IT CAN BE REPRODUCED WORD FOR WORD.</p>}
            </div>
            <Colophon kind={doc.category} title={doc.title} author={doc.author} date={doc.updated_at} words={doc.word_count} verified={doc.verbatim_verified} path={`/u99/documents/${doc.slug}`} />
            <div className="mt-10 pt-8 border-t border-white/[0.07] flex flex-wrap gap-3">
              <Link to="/u99/documents" className={`${btn} border-white/15 text-slate-300 hover:text-amber-300 hover:border-amber-400/50`} data-testid="document-return-bottom-btn"><CornerUpLeft size={13} /> Return to Documents</Link>
              {hasFile && <a href={doc.file_url} download={doc.file_name || true} className={`${btn} border-transparent text-slate-500 hover:text-slate-200`}><Download size={13} /> Download</a>}
            </div>
          </article>
        </>
      )}
      {!doc && !error && <div className="pt-48 pb-48 text-center font-mono-precision text-xs tracking-[0.3em] text-slate-600">OPENING…</div>}
    </main>
  );
}
